import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../../lib/api';
import { CATEGORY_LABELS, PRODUCT_STATUS_CONFIG } from './storeConstants';

const EMPTY_FORM = {
  category: '', brand: '', product_line: '', model: '', product_code: '',
  description: '', description_technical: '', power_label: '', capacity_label: '', unit: 'pz',
  base_price: '', promo_price: '', customer_price: '', vat_rate: 10,
  classe_energetica: '', wifi: false, image_url: '', datasheet_url: '',
  thermostat_included: false, thermostat_optional: false, thermostat_model: '', thermostat_price: '',
  installation_included: true,
  tasso_zero_available: false, financing_months: '', fido_in_bolletta: false, fido_months: '',
  promo_code: '', promo_valid_from: '', promo_valid_to: '',
  voucher_available: false, voucher_amount: '', voucher_conditions: '', zonal_discount: '',
  status: 'ATTIVO', visible_to_agents: true, is_featured: false,
  available_from: '', available_to: '',
  note_internal: '', note_commercial: '', note_pdf: '',
};

const productToForm = (p) => ({
  ...EMPTY_FORM,
  category: p.category || '',
  brand: p.brand || '',
  product_line: p.product_line || '',
  model: p.model || '',
  product_code: p.product_code || '',
  description: p.description || '',
  description_technical: p.description_technical || '',
  power_label: p.power_label || '',
  capacity_label: p.capacity_label || '',
  unit: p.unit || 'pz',
  base_price: p.base_price ?? '',
  promo_price: p.promo_price ?? '',
  customer_price: p.customer_price ?? '',
  vat_rate: p.vat_rate ?? 10,
  classe_energetica: p.technical_specs?.classe_energetica || '',
  wifi: p.technical_specs?.wifi || false,
  image_url: p.image_url || '',
  datasheet_url: p.datasheet_url || '',
  thermostat_included: p.thermostat?.included || false,
  thermostat_optional: p.thermostat?.optional || false,
  thermostat_model: p.thermostat?.model_name || '',
  thermostat_price: p.thermostat?.additional_price ?? '',
  installation_included: p.installation_included ?? true,
  tasso_zero_available: p.tasso_zero_available || false,
  financing_months: p.financing_months ?? '',
  fido_in_bolletta: p.fido_in_bolletta || false,
  fido_months: p.fido_months ?? '',
  promo_code: p.promo_code || '',
  promo_valid_from: p.promo_valid_from || '',
  promo_valid_to: p.promo_valid_to || '',
  voucher_available: p.voucher_available || false,
  voucher_amount: p.voucher_amount ?? '',
  voucher_conditions: p.voucher_conditions || '',
  zonal_discount: p.zonal_discount ?? '',
  status: p.status || 'ATTIVO',
  visible_to_agents: p.visible_to_agents ?? true,
  is_featured: p.is_featured || false,
  available_from: p.available_from || '',
  available_to: p.available_to || '',
  note_internal: p.note_internal || '',
  note_commercial: p.note_commercial || '',
  note_pdf: p.note_pdf || '',
});

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const formToPayload = (f, existing) => ({
  category: f.category,
  brand: f.brand,
  product_line: f.product_line || null,
  model: f.model,
  product_code: f.product_code,
  description: f.description || null,
  description_technical: f.description_technical || null,
  power_label: f.power_label || null,
  capacity_label: f.capacity_label || null,
  unit: f.unit || 'pz',
  base_price: num(f.base_price),
  promo_price: num(f.promo_price),
  customer_price: num(f.customer_price),
  vat_rate: Number(f.vat_rate),
  technical_specs: {
    ...(existing?.technical_specs || {}),
    classe_energetica: f.classe_energetica || null,
    wifi: f.wifi,
  },
  thermostat: {
    included: f.thermostat_included,
    optional: f.thermostat_optional,
    model_name: f.thermostat_model || null,
    additional_price: num(f.thermostat_price) || 0,
  },
  installation_included: f.installation_included,
  tasso_zero_available: f.tasso_zero_available,
  financing_months: f.tasso_zero_available ? (num(f.financing_months) || 24) : null,
  financing_available: f.tasso_zero_available ? [`TASSO_ZERO_${num(f.financing_months) || 24}`] : [],
  fido_in_bolletta: f.fido_in_bolletta,
  fido_months: f.fido_in_bolletta ? (num(f.fido_months) || 48) : null,
  promo_code: f.promo_code || null,
  promo_valid_from: f.promo_valid_from || null,
  promo_valid_to: f.promo_valid_to || null,
  voucher_available: f.voucher_available,
  voucher_amount: f.voucher_available ? (num(f.voucher_amount) || 0) : 0,
  voucher_conditions: f.voucher_conditions || null,
  zonal_discount: num(f.zonal_discount) || 0,
  image_url: f.image_url || null,
  datasheet_url: f.datasheet_url || null,
  status: f.status,
  visible_to_agents: f.visible_to_agents,
  is_featured: f.is_featured,
  available_from: f.available_from || null,
  available_to: f.available_to || null,
  note_internal: f.note_internal || null,
  note_commercial: f.note_commercial || null,
  note_pdf: f.note_pdf || null,
});

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const SwitchField = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2 cursor-pointer">
    <span className="text-sm">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

export const ProductFormDialog = ({ open, onOpenChange, product, meta, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(product?.id);

  useEffect(() => {
    if (open) {
      setForm(product ? productToForm(product) : EMPTY_FORM);
    }
  }, [open, product]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setInput = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.product_code || !form.category || !form.brand || !form.model || form.base_price === '') {
      toast.error('Compila i campi obbligatori: codice, categoria, marca, modello, prezzo base');
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form, product);
      if (isEdit) {
        await storeApi.updateProduct(product.id, payload);
        toast.success('Prodotto aggiornato');
      } else {
        await storeApi.createProduct(payload);
        toast.success('Prodotto creato');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifica Prodotto — ${product.product_code}` : 'Nuovo Prodotto Store'}</DialogTitle>
          <DialogDescription>
            Scheda prodotto organizzata per sezioni. I campi con * sono obbligatori.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="base" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="base" data-testid="tab-dati-base">Dati Base</TabsTrigger>
            <TabsTrigger value="tech" data-testid="tab-tecnici">Tecnici</TabsTrigger>
            <TabsTrigger value="prices" data-testid="tab-prezzi">Prezzi</TabsTrigger>
            <TabsTrigger value="promo" data-testid="tab-promo">Promo</TabsTrigger>
            <TabsTrigger value="visibility" data-testid="tab-visibilita">Visibilità</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-note">Note</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto py-4 pr-1">
            {/* A. DATI BASE */}
            <TabsContent value="base" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Categoria *">
                  <Select value={form.category} onValueChange={set('category')}>
                    <SelectTrigger data-testid="form-category"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {(meta?.categories || []).map((c) => (
                        <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Codice Prodotto *">
                  <Input value={form.product_code} onChange={setInput('product_code')} disabled={isEdit} data-testid="form-product-code" />
                </Field>
                <Field label="Marca *">
                  <Input value={form.brand} onChange={setInput('brand')} placeholder="es. HAIER" data-testid="form-brand" />
                </Field>
                <Field label="Linea">
                  <Input value={form.product_line} onChange={setInput('product_line')} placeholder="es. PEARL" data-testid="form-line" />
                </Field>
              </div>
              <Field label="Modello *">
                <Input value={form.model} onChange={setInput('model')} placeholder="es. Mono Split 3,5 kW" data-testid="form-model" />
              </Field>
              <Field label="Descrizione commerciale">
                <Textarea rows={3} value={form.description} onChange={setInput('description')} data-testid="form-description" />
              </Field>
            </TabsContent>

            {/* B. DATI TECNICI */}
            <TabsContent value="tech" className="space-y-4 mt-0">
              <div className="grid grid-cols-3 gap-4">
                <Field label="Potenza"><Input value={form.power_label} onChange={setInput('power_label')} placeholder="es. 30 kW" /></Field>
                <Field label="Capacità"><Input value={form.capacity_label} onChange={setInput('capacity_label')} placeholder="es. 100 l" /></Field>
                <Field label="Unità di misura"><Input value={form.unit} onChange={setInput('unit')} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Classe Energetica"><Input value={form.classe_energetica} onChange={setInput('classe_energetica')} placeholder="es. A+++/A++" /></Field>
                <div className="flex items-end">
                  <SwitchField label="WiFi / Smart" checked={form.wifi} onChange={set('wifi')} />
                </div>
              </div>
              <Field label="Descrizione tecnica">
                <Textarea rows={3} value={form.description_technical} onChange={setInput('description_technical')} />
              </Field>
              <Field label="URL Scheda Tecnica (datasheet)">
                <Input value={form.datasheet_url} onChange={setInput('datasheet_url')} placeholder="https://..." />
              </Field>
              <Field label="URL Immagine Prodotto">
                <Input value={form.image_url} onChange={setInput('image_url')} placeholder="https://..." />
              </Field>
              {form.image_url && (
                <div className="h-28 border rounded-lg flex items-center justify-center bg-gray-50">
                  <img src={form.image_url} alt="anteprima" className="h-full object-contain p-2" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </TabsContent>

            {/* C. PREZZI */}
            <TabsContent value="prices" className="space-y-4 mt-0">
              <div className="grid grid-cols-3 gap-4">
                <Field label="Prezzo Base * (€)"><Input type="number" value={form.base_price} onChange={setInput('base_price')} data-testid="form-base-price" /></Field>
                <Field label="Prezzo Promo (€)"><Input type="number" value={form.promo_price} onChange={setInput('promo_price')} data-testid="form-promo-price" /></Field>
                <Field label="Prezzo Cliente Default (€)"><Input type="number" value={form.customer_price} onChange={setInput('customer_price')} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="IVA %">
                  <Select value={String(form.vat_rate)} onValueChange={(v) => set('vat_rate')(Number(v))}>
                    <SelectTrigger data-testid="form-vat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 4, 10, 22].map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Sconto Zonale (€)"><Input type="number" value={form.zonal_discount} onChange={setInput('zonal_discount')} /></Field>
              </div>
              <div className="border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Termostato</p>
                <div className="grid grid-cols-2 gap-3">
                  <SwitchField label="Termostato incluso" checked={form.thermostat_included} onChange={set('thermostat_included')} />
                  <SwitchField label="Termostato opzionale" checked={form.thermostat_optional} onChange={set('thermostat_optional')} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Modello termostato"><Input value={form.thermostat_model} onChange={setInput('thermostat_model')} placeholder="es. TADO, CUBE S NET" /></Field>
                  <Field label="Prezzo termostato (€)"><Input type="number" value={form.thermostat_price} onChange={setInput('thermostat_price')} /></Field>
                </div>
              </div>
              <SwitchField label="Installazione inclusa nel prezzo" checked={form.installation_included} onChange={set('installation_included')} />
            </TabsContent>

            {/* D. PROMO */}
            <TabsContent value="promo" className="space-y-4 mt-0">
              <Field label="Promozione collegata">
                <Select value={form.promo_code || 'none'} onValueChange={(v) => set('promo_code')(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="form-promo-code"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna</SelectItem>
                    {(meta?.promotions || []).map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.code} — {p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Promo valida dal"><Input type="date" value={form.promo_valid_from} onChange={setInput('promo_valid_from')} /></Field>
                <Field label="Promo valida al"><Input type="date" value={form.promo_valid_to} onChange={setInput('promo_valid_to')} /></Field>
              </div>
              <div className="border rounded-lg p-3 space-y-3">
                <SwitchField label="Voucher disponibile" checked={form.voucher_available} onChange={set('voucher_available')} />
                {form.voucher_available && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Valore voucher (€)"><Input type="number" value={form.voucher_amount} onChange={setInput('voucher_amount')} /></Field>
                    <Field label="Condizioni voucher" className="col-span-2">
                      <Textarea rows={2} value={form.voucher_conditions} onChange={setInput('voucher_conditions')} />
                    </Field>
                  </div>
                )}
              </div>
              <div className="border rounded-lg p-3 space-y-3">
                <SwitchField label="Tasso Zero disponibile" checked={form.tasso_zero_available} onChange={set('tasso_zero_available')} />
                {form.tasso_zero_available && (
                  <Field label="Durata finanziamento (mesi)"><Input type="number" value={form.financing_months} onChange={setInput('financing_months')} placeholder="24" /></Field>
                )}
              </div>
              <div className="border rounded-lg p-3 space-y-3">
                <SwitchField label="FIDO in bolletta" checked={form.fido_in_bolletta} onChange={set('fido_in_bolletta')} />
                {form.fido_in_bolletta && (
                  <Field label="Durata FIDO (mesi)"><Input type="number" value={form.fido_months} onChange={setInput('fido_months')} placeholder="48" /></Field>
                )}
              </div>
            </TabsContent>

            {/* E. VISIBILITÀ */}
            <TabsContent value="visibility" className="space-y-4 mt-0">
              <Field label="Stato prodotto">
                <Select value={form.status} onValueChange={set('status')}>
                  <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta?.statuses || Object.keys(PRODUCT_STATUS_CONFIG)).map((s) => (
                      <SelectItem key={s} value={s}>{PRODUCT_STATUS_CONFIG[s]?.label || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <SwitchField label="Visibile agli agenti store" checked={form.visible_to_agents} onChange={set('visible_to_agents')} testId="form-visible-agents" />
              <SwitchField label="Prodotto in evidenza (Top)" checked={form.is_featured} onChange={set('is_featured')} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Disponibile dal"><Input type="date" value={form.available_from} onChange={setInput('available_from')} /></Field>
                <Field label="Disponibile fino al"><Input type="date" value={form.available_to} onChange={setInput('available_to')} /></Field>
              </div>
            </TabsContent>

            {/* F. NOTE */}
            <TabsContent value="notes" className="space-y-4 mt-0">
              <Field label="Note interne (non visibili agli agenti)">
                <Textarea rows={3} value={form.note_internal} onChange={setInput('note_internal')} />
              </Field>
              <Field label="Note per agente (commerciali)">
                <Textarea rows={3} value={form.note_commercial} onChange={setInput('note_commercial')} />
              </Field>
              <Field label="Note da mostrare nel PDF cliente">
                <Textarea rows={3} value={form.note_pdf} onChange={setInput('note_pdf')} />
              </Field>
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvataggio...' : isEdit ? 'Salva Modifiche' : 'Crea Prodotto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
